/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Queues, and who staffs them.
 *
 * A queue with no members is the quietest possible failure: the caller reaches
 * it, hears hold music, and waits out max_wait_seconds while the router finds
 * nobody to offer the call to. Nothing rings, nothing errors, and the call
 * record just says "queued". That is why membership is the primary thing this
 * page shows, rather than a detail hidden behind an edit dialog.
 *
 * Routing RULES -- the conditions that pick which queue a call lands in -- are
 * not here. `routing_rules` exists as a model and is evaluated on the inbound
 * webhook, but has no CRUD endpoints, so there is nothing to drive yet.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { Add, ArrowBack, Delete, Edit, GroupAdd, WarningAmber } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage } from "utils/apiErrors";

import colors from "styles/tokens/colors";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const CARD_BORDER = `1px solid ${colors.callCenter.cardBorder}`;

const STRATEGIES = [
  { value: "longest_idle", label: "Longest idle" },
  { value: "round_robin", label: "Round robin" },
  { value: "fewest_active", label: "Fewest active calls" },
  { value: "skills_then_longest_idle", label: "Skills, then longest idle" },
];

/** The User schema carries first_name/last_name, not a composed full_name. */
const userLabel = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.username || `User ${u.id}`;

/**
 * Create or edit a queue.
 *
 * Only the fields that change what a caller experiences. The rest of QueueBase
 * (hold music, announcements, SLA, skills) is real but has no story on this
 * page yet, and a form full of inputs nobody can explain is worse than a
 * shorter one.
 *
 * `queue_key` is deliberately absent. The server derives it from the name and
 * refuses to change it afterwards, because it is baked into the TwiML wait
 * URLs of calls that are in progress right now.
 */
function QueueFormDialog({ open, onClose, onSave, queue, queues, saving }) {
  const editing = Boolean(queue);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: queue?.name || "",
      description: queue?.description || "",
      routing_strategy: queue?.routing_strategy || "longest_idle",
      priority: queue?.priority ?? 5,
      max_wait_seconds: queue?.max_wait_seconds ?? 300,
      ring_timeout_seconds: queue?.ring_timeout_seconds ?? 20,
      max_ring_attempts: queue?.max_ring_attempts ?? 3,
      wrap_up_seconds: queue?.wrap_up_seconds ?? 30,
      overflow_queue_id: queue?.overflow_queue_id || "",
      voicemail_enabled: queue?.voicemail_enabled ?? true,
    });
  }, [open, queue]);

  if (!form) return null;

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));
  const setNumber = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value === "" ? "" : Number(e.target.value) }));

  // A queue cannot overflow into itself: the call would bounce between the
  // same set of agents until max_wait. The server rejects it too, but saying
  // so before the round trip is kinder.
  const overflowOptions = queues.filter((q) => q.id !== queue?.id && q.is_active);

  const valid = form.name.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Edit ${queue.name}` : "New queue"}</DialogTitle>
      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Name"
            size="small"
            fullWidth
            required
            value={form.name}
            onChange={set("name")}
            helperText={
              editing
                ? `Internal key stays ${queue.queue_key}; it is used by calls already in progress.`
                : "The key is derived from this and cannot be changed later."
            }
          />

          <TextField
            label="Description"
            size="small"
            fullWidth
            value={form.description}
            onChange={set("description")}
          />

          <TextField
            select
            label="Routing strategy"
            size="small"
            fullWidth
            value={form.routing_strategy}
            onChange={set("routing_strategy")}
            helperText="Only matters once a queue has more than one agent."
          >
            {STRATEGIES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Ring each agent for"
                size="small"
                fullWidth
                type="number"
                value={form.ring_timeout_seconds}
                onChange={setNumber("ring_timeout_seconds")}
                InputProps={{ endAdornment: "s" }}
                inputProps={{ min: 5, max: 120 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Ring attempts"
                size="small"
                fullWidth
                type="number"
                value={form.max_ring_attempts}
                onChange={setNumber("max_ring_attempts")}
                inputProps={{ min: 1, max: 10 }}
                helperText="Before overflow or voicemail."
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Hold callers for up to"
                size="small"
                fullWidth
                type="number"
                value={form.max_wait_seconds}
                onChange={setNumber("max_wait_seconds")}
                InputProps={{ endAdornment: "s" }}
                inputProps={{ min: 10, max: 7200 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Wrap-up time"
                size="small"
                fullWidth
                type="number"
                value={form.wrap_up_seconds}
                onChange={setNumber("wrap_up_seconds")}
                InputProps={{ endAdornment: "s" }}
                inputProps={{ min: 0, max: 600 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Priority"
                size="small"
                fullWidth
                value={form.priority}
                onChange={setNumber("priority")}
                helperText="1 is answered first."
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Overflow to"
                size="small"
                fullWidth
                value={form.overflow_queue_id}
                onChange={set("overflow_queue_id")}
                helperText="When ring attempts run out."
              >
                <MenuItem value="">
                  <em>Nowhere</em>
                </MenuItem>
                {overflowOptions.map((q) => (
                  <MenuItem key={q.id} value={q.id}>
                    {q.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox>
              <MDTypography variant="button" fontWeight="medium" display="block">
                Voicemail
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Offer voicemail instead of dropping the caller.
              </MDTypography>
            </MDBox>
            <Switch
              checked={Boolean(form.voicemail_enabled)}
              onChange={(e) =>
                setForm((f) => ({ ...f, voicemail_enabled: e.target.checked }))
              }
            />
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="dark" onClick={onClose}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          disabled={!valid || saving}
          onClick={() =>
            onSave({
              ...form,
              overflow_queue_id: form.overflow_queue_id || null,
            })
          }
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function AddMemberDialog({ open, onClose, onAdd, users, teams, existing, saving }) {
  const [kind, setKind] = useState("user");
  const [userId, setUserId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [includeSubteams, setIncludeSubteams] = useState("no");
  const [role, setRole] = useState("agent");

  useEffect(() => {
    if (open) {
      setKind("user");
      setUserId("");
      setTeamId("");
      setIncludeSubteams("no");
      setRole("agent");
    }
  }, [open]);

  // Adding the same person twice is accepted by the server and then deduped by
  // resolve_agent_ids, so it is silently useless -- better to not offer it.
  const alreadyIn = useMemo(
    () => new Set(existing.filter((m) => m.user_id).map((m) => m.user_id)),
    [existing]
  );
  const availableUsers = users.filter((u) => !alreadyIn.has(u.id));

  const valid = kind === "user" ? Boolean(userId) : Boolean(teamId);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add to queue</DialogTitle>
      <DialogContent>
        <TextField
          select
          fullWidth
          size="small"
          label="Add"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          margin="normal"
        >
          <MenuItem value="user">A person</MenuItem>
          <MenuItem value="team">A team</MenuItem>
        </TextField>

        {kind === "user" ? (
          <TextField
            select
            fullWidth
            size="small"
            label="Person"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            margin="normal"
            helperText={
              availableUsers.length ? " " : "Everyone available is already in this queue."
            }
          >
            {availableUsers.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {userLabel(u)}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <>
            <TextField
              select
              fullWidth
              size="small"
              label="Team"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              margin="normal"
            >
              {teams.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="Sub-teams"
              value={includeSubteams}
              onChange={(e) => setIncludeSubteams(e.target.value)}
              margin="normal"
              helperText="Membership resolves on every call, so team changes apply immediately."
            >
              <MenuItem value="no">This team only</MenuItem>
              <MenuItem value="yes">Include sub-teams</MenuItem>
            </TextField>
          </>
        )}

        <TextField
          select
          fullWidth
          size="small"
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          margin="normal"
          helperText={
            role === "agent"
              ? "Agents are offered calls from this queue."
              : "Supervisors can monitor and barge, but are not offered calls."
          }
        >
          <MenuItem value="agent">Agent</MenuItem>
          <MenuItem value="supervisor">Supervisor</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="dark" onClick={onClose}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          disabled={!valid || saving}
          onClick={() =>
            onAdd(
              kind === "user"
                ? { user_id: Number(userId), member_role: role }
                : {
                    team_id: Number(teamId),
                    include_subteams: includeSubteams === "yes",
                    member_role: role,
                  }
            )
          }
        >
          Add
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function QueueCard({ queue, users, teams, onChanged, onEdit, setError, setToast, axiosInstance }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [strategy, setStrategy] = useState(queue.routing_strategy);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/call-center/queues/${queue.id}/members`);
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not load queue members."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, queue.id, setError]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const addMember = async (payload) => {
    setSaving(true);
    setError("");
    try {
      await axiosInstance.post(`/call-center/queues/${queue.id}/members`, payload);
      setDialogOpen(false);
      await loadMembers();
      onChanged();
      setToast("Added to the queue.");
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not add that member."));
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (member) => {
    setError("");
    try {
      await axiosInstance.delete(`/call-center/queues/${queue.id}/members/${member.id}`);
      await loadMembers();
      onChanged();
      setToast("Removed from the queue.");
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not remove that member."));
    }
  };

  const saveStrategy = async (next) => {
    setStrategy(next);
    setError("");
    try {
      await axiosInstance.put(`/call-center/queues/${queue.id}`, { routing_strategy: next });
      setToast("Routing strategy saved.");
    } catch (err) {
      setStrategy(queue.routing_strategy);
      setError(getApiErrorMessage(err, "Could not save the routing strategy."));
    }
  };

  const agents = members.filter((m) => m.member_role === "agent");
  const unstaffed = !loading && agents.length === 0;

  return (
    <Card sx={{ p: 3, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px", mb: 3 }}>
      <MDBox display="flex" alignItems="flex-start" gap={1} mb={1}>
        <MDBox flexGrow={1}>
          <MDTypography variant="h6" fontWeight="medium">
            {queue.name}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {queue.queue_key} · {queue.channel} · waits up to {queue.max_wait_seconds}s · rings
            each agent for {queue.ring_timeout_seconds}s
          </MDTypography>
        </MDBox>
        <Chip
          size="small"
          color={queue.available_agents ? "success" : "default"}
          variant={queue.available_agents ? "filled" : "outlined"}
          label={`${queue.available_agents} available now`}
        />
        <Tooltip title="Edit this queue">
          <IconButton size="small" onClick={() => onEdit(queue)} aria-label="Edit queue">
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        <MDButton
          variant="outlined"
          color="info"
          size="small"
          startIcon={<GroupAdd />}
          onClick={() => setDialogOpen(true)}
        >
          Add member
        </MDButton>
      </MDBox>

      {unstaffed ? (
        <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
          Nobody staffs this queue, so calls that reach it are never offered to anyone. The caller
          hears hold music until the {queue.max_wait_seconds}s wait expires.
        </Alert>
      ) : null}

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <MDBox display="flex" justifyContent="center" py={3}>
          <CircularProgress size={22} />
        </MDBox>
      ) : members.length === 0 ? (
        <MDTypography variant="body2" color="text">
          No members yet.
        </MDTypography>
      ) : (
        <MDBox display="flex" flexDirection="column" gap={1}>
          {members.map((m) => (
            <MDBox
              key={m.id}
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ borderBottom: "1px solid #F2F4F8", pb: 1 }}
            >
              <MDBox flexGrow={1}>
                <MDTypography variant="button" fontWeight="medium">
                  {m.display_name || (m.team_id ? `Team ${m.team_id}` : `User ${m.user_id}`)}
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block">
                  {m.team_id
                    ? `Team${m.include_subteams ? " (with sub-teams)" : ""}`
                    : "Person"}
                  {" · "}
                  {m.member_role}
                </MDTypography>
              </MDBox>
              <Tooltip
                title={
                  m.member_role === "supervisor"
                    ? "Supervisors are not offered calls."
                    : "Offered calls from this queue."
                }
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={m.member_role === "supervisor" ? "Supervisor" : "Agent"}
                />
              </Tooltip>
              <Tooltip title="Remove from queue">
                <IconButton size="small" onClick={() => removeMember(m)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </MDBox>
          ))}
        </MDBox>
      )}

      <TextField
        select
        size="small"
        label="Routing strategy"
        value={strategy}
        onChange={(e) => saveStrategy(e.target.value)}
        margin="normal"
        sx={{ minWidth: 260 }}
        helperText="Which of the free agents gets offered the call first."
      >
        {STRATEGIES.map((s) => (
          <MenuItem key={s.value} value={s.value}>
            {s.label}
          </MenuItem>
        ))}
      </TextField>

      <AddMemberDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdd={addMember}
        users={users}
        teams={teams}
        existing={members}
        saving={saving}
      />
    </Card>
  );
}

function TelephonyRoutingPage() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingQueue, setEditingQueue] = useState(null);
  const [savingQueue, setSavingQueue] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const queuesRes = await axiosInstance.get("/call-center/queues", {
        params: { include_inactive: true },
      });
      setQueues(Array.isArray(queuesRes.data) ? queuesRes.data : []);

      // Only feed the pickers. A failure here must not hide the queues.
      const [usersRes, teamsRes] = await Promise.allSettled([
        axiosInstance.get("/users/"),
        axiosInstance.get("/teams"),
      ]);
      setUsers(
        usersRes.status === "fulfilled" && Array.isArray(usersRes.value.data)
          ? usersRes.value.data.filter((u) => u.is_active && !u.is_support_user)
          : []
      );
      setTeams(
        teamsRes.status === "fulfilled" && Array.isArray(teamsRes.value.data)
          ? teamsRes.value.data
          : []
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to load queues."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  const openNew = () => {
    setEditingQueue(null);
    setFormOpen(true);
  };

  const openEdit = (queue) => {
    setEditingQueue(queue);
    setFormOpen(true);
  };

  const saveQueue = async (payload) => {
    setSavingQueue(true);
    setError("");
    try {
      if (editingQueue) {
        await axiosInstance.put(`/call-center/queues/${editingQueue.id}`, payload);
        setToast(`${payload.name} saved.`);
      } else {
        await axiosInstance.post("/call-center/queues", payload);
        setToast(`${payload.name} created. Add agents to it before pointing a number at it.`);
      }
      setFormOpen(false);
      setEditingQueue(null);
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not save the queue."));
    } finally {
      setSavingQueue(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const unstaffedCount = queues.filter((q) => !q.member_count).length;

  // The header's actions, shared by the v2 page header and the classic one.
  const headerActions = (
    <>
      <MDButton variant="gradient" color="info" startIcon={<Add />} onClick={openNew}>
        New queue
      </MDButton>
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={isV2 ? 0 : 3}>
        {isV2 ? (
          <PageHeader
            title="Queues & routing"
            summary="A queue only rings the people in it. Everything else about a call can be correct and it will still sit on hold if nobody is a member."
            actions={headerActions}
          />
        ) : (
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <IconButton onClick={() => navigate("/settings/telephony")}>
              <ArrowBack />
            </IconButton>
            <MDBox flexGrow={1}>
              <MDTypography variant="h5" fontWeight="medium">
                Queues &amp; routing
              </MDTypography>
              <MDTypography variant="body2" color="text">
                A queue only rings the people in it. Everything else about a call can be correct and
                it will still sit on hold if nobody is a member.
              </MDTypography>
            </MDBox>
            {headerActions}
          </MDBox>
        )}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {!loading && unstaffedCount > 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {unstaffedCount === 1
              ? "One queue has no members."
              : `${unstaffedCount} queues have no members.`}{" "}
            Calls reaching them are never offered to anyone.
          </Alert>
        ) : null}

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </MDBox>
        ) : queues.length === 0 ? (
          <Card sx={{ p: 4, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px" }}>
            <MDBox textAlign="center" py={4}>
              <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                No queues yet
              </MDTypography>
              <MDTypography variant="body2" color="text" mb={3}>
                Inbound calls need a queue to land in, and a number pointed at no queue drops
                every call it receives.
              </MDTypography>
              <MDButton variant="gradient" color="info" startIcon={<Add />} onClick={openNew}>
                Create a queue
              </MDButton>
            </MDBox>
          </Card>
        ) : (
          <Grid container>
            <Grid item xs={12} lg={8}>
              {queues.map((q) => (
                <QueueCard
                  key={q.id}
                  queue={q}
                  users={users}
                  teams={teams}
                  axiosInstance={axiosInstance}
                  onChanged={load}
                  onEdit={openEdit}
                  setError={setError}
                  setToast={setToast}
                />
              ))}
              <MDTypography variant="caption" color="text">
                Routing rules — the conditions that choose which queue a call lands in — are not
                editable yet; inbound calls use the number&apos;s default queue.
              </MDTypography>
            </Grid>
          </Grid>
        )}
      </MDBox>

      <QueueFormDialog
        open={formOpen}
        queue={editingQueue}
        queues={queues}
        saving={savingQueue}
        onClose={() => {
          setFormOpen(false);
          setEditingQueue(null);
        }}
        onSave={saveQueue}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setToast("")}>
          {toast}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default TelephonyRoutingPage;
